import React, { useRef, useState } from "react";
import {
  addCategory, changeCategoryImage, deleteCategory, moveCategory, uploadImage,
} from "../../../api/products";
import { errorMessage } from "../../../api";
import { showToast } from "../../common/components/Toast";

const DEFAULT_IMAGE = "/images/DEFAULT.png";

const CategoryManageModal = ({ isOpen, onClose, categories, onChanged }) => {
  const [newName, setNewName] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  // 파일 입력은 ref로 직접 다룬다. key로 리마운트시키면 이미 열려 있던
  // 파일 선택창이 닫혀버려 "창이 안 뜬다"처럼 보인다.
  const fileInputRef = useRef(null);
  // 기존 카테고리의 이미지 교체용 (어느 카테고리를 바꾸는 중인지 기억한다)
  const replaceInputRef = useRef(null);
  const [replaceTarget, setReplaceTarget] = useState(null);
  const [busyName, setBusyName] = useState(null);
  const [isMoving, setIsMoving] = useState(false);

  if (!isOpen) return null;

  const resetForm = () => {
    setNewName("");
    setImageFile(null);
    setPreviewUrl(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0] || null;
    // 서버 한도(8MB)를 넘기면 업로드가 거부되므로 미리 걸러 안내한다
    if (file && file.size > 8 * 1024 * 1024) {
      showToast("이미지 용량이 너무 큽니다. 8MB 이하로 올려주세요.", "fail");
      e.target.value = "";
      setImageFile(null);
      setPreviewUrl(null);
      return;
    }
    setImageFile(file);
    setPreviewUrl(file ? URL.createObjectURL(file) : null);
  };

  // 순서 변경 — 서버가 이웃과 맞바꾼 뒤 목록을 다시 받아온다.
  // 한 번의 이동이 두 행을 건드리므로, 응답이 올 때까지 목록 전체의 화살표를 잠근다.
  // (누른 행만 잠그면 바로 옆 행을 연달아 눌러 서로의 갱신을 덮어쓸 수 있다)
  const handleMove = async (name, direction) => {
    setIsMoving(true);
    try {
      const result = await moveCategory(name, direction);
      if (!result.moved) {
        showToast(result.message, "fail");
      }
      // 이동 실패(이미 맨 끝)는 화면과 서버가 어긋났다는 뜻이므로 이때도 목록을 다시 받는다
      await onChanged();
    } catch (error) {
      showToast(errorMessage(error, "순서 변경에 실패했습니다."), "fail");
    } finally {
      setIsMoving(false);
    }
  };

  // 이미지 교체 — 어떤 카테고리를 바꿀지 정한 뒤 파일창을 연다
  const startReplaceImage = (name) => {
    setReplaceTarget(name);
    // 같은 파일을 다시 골라도 change가 발생하도록 값을 비운다
    if (replaceInputRef.current) replaceInputRef.current.value = "";
    replaceInputRef.current?.click();
  };

  const handleReplaceFile = async (e) => {
    const file = e.target.files[0] || null;
    const name = replaceTarget;
    setReplaceTarget(null);
    if (!file || !name) return;
    if (file.size > 8 * 1024 * 1024) {
      showToast("이미지 용량이 너무 큽니다. 8MB 이하로 올려주세요.", "fail");
      return;
    }

    setBusyName(name);
    try {
      const imageUrl = await uploadImage(file);
      if (!imageUrl) {
        showToast("이미지 업로드에 실패했습니다.", "fail");
        return;
      }
      await changeCategoryImage(name, imageUrl);
      showToast("이미지가 변경되었습니다.", "success");
      await onChanged();
    } catch (error) {
      showToast(errorMessage(error, "이미지 변경에 실패했습니다."), "fail");
    } finally {
      setBusyName(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmedName = newName.trim();
    if (!trimmedName) {
      showToast("카테고리 이름을 입력해 주세요!", "fail");
      return;
    }

    setIsSaving(true);
    try {
      // 이미지는 상품 이미지와 같은 업로드 경로를 쓴다. 없으면 대표 이미지 없이 등록.
      let imageUrl = null;
      if (imageFile) {
        imageUrl = await uploadImage(imageFile);
        if (!imageUrl) {
          showToast("이미지 업로드에 실패했습니다.", "fail");
          return;
        }
      }

      await addCategory(trimmedName, imageUrl);
      showToast("카테고리가 추가되었습니다!", "success");
      resetForm();
      await onChanged();
    } catch (error) {
      // 중복·길이 판정은 서버가 하므로 서버가 준 사유를 그대로 보여준다
      showToast(errorMessage(error, "카테고리 추가에 실패했습니다."), "fail");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    const targetName = deleteTarget;
    setDeleteTarget(null);

    try {
      await deleteCategory(targetName);
      showToast("카테고리가 삭제되었습니다.", "success");
      await onChanged();
    } catch (error) {
      // 상품이 남아 있을 때의 안내 문구가 서버 응답에 담겨 온다
      showToast(errorMessage(error, "카테고리 삭제에 실패했습니다."), "fail");
    }
  };

  return (
    <>
      <div className="modal-overlay">
        <div className="modal-content">
          <h2>카테고리 관리</h2>

          <form onSubmit={handleSubmit}>
            <input
              type="text"
              name="name"
              placeholder="카테고리 이름"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              maxLength={50}
              className="w-full px-6 py-3 text-base text-gray-900 border border-gray-900 rounded-2xl placeholder:text-gray-400"
            />
            {/* label을 눌러 파일창을 연다. JS로 숨은 입력을 click()하는 방식은
                브라우저에 따라 차단되므로, 브라우저가 직접 여는 label 방식을 쓴다.
                입력은 display:none 대신 화면 밖으로 밀어 접근성·동작을 유지한다. */}
            <input
              ref={fileInputRef}
              id="category-image-input"
              type="file"
              name="imageFile"
              accept="image/*"
              onChange={handleFileChange}
              style={{ position: "absolute", width: 1, height: 1, opacity: 0, pointerEvents: "none" }}
            />
            <div className="flex items-center gap-2 my-2">
              <label
                htmlFor="category-image-input"
                className="px-5 py-2 text-sm border border-gray-900 text-gray-900 rounded-2xl cursor-pointer hover:bg-gray-900 hover:text-white whitespace-nowrap"
              >
                이미지 선택
              </label>
              <span className="text-sm text-gray-500">
                {imageFile ? imageFile.name : "선택된 이미지 없음 (기본 이미지로 표시)"}
              </span>
            </div>
            {previewUrl && (
              <img src={previewUrl} alt="미리보기" className="w-16 h-16 object-contain my-2" />
            )}

          {/* 기존 카테고리의 이미지 교체용 숨은 입력 — 어느 행이든 이 하나를 공유한다 */}
          <input
            ref={replaceInputRef}
            type="file"
            accept="image/*"
            onChange={handleReplaceFile}
            style={{ position: "absolute", width: 1, height: 1, opacity: 0, pointerEvents: "none" }}
          />

          <div className="mt-4 max-h-[280px] overflow-y-auto">
            {categories.map(({ category, image_url }, index) => (
              <div key={category} className="flex items-center justify-between py-2 border-t">
                <div className="flex items-center gap-2 min-w-0">
                  <img
                    src={image_url || DEFAULT_IMAGE}
                    alt={category}
                    className="w-8 h-8 object-contain shrink-0"
                    onError={(e) => (e.target.src = DEFAULT_IMAGE)}
                  />
                  <span className="text-sm truncate">{category}</span>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    type="button"
                    className="text-sm text-gray-700 px-2 py-1 rounded-md border disabled:text-gray-300"
                    title="위로"
                    disabled={index === 0 || isMoving || busyName === category}
                    onClick={() => handleMove(category, "up")}
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    className="text-sm text-gray-700 px-2 py-1 rounded-md border disabled:text-gray-300"
                    title="아래로"
                    disabled={index === categories.length - 1 || isMoving || busyName === category}
                    onClick={() => handleMove(category, "down")}
                  >
                    ↓
                  </button>
                  <button
                    type="button"
                    className="text-sm text-gray-700 px-2 py-1 rounded-md border"
                    disabled={busyName === category}
                    onClick={() => startReplaceImage(category)}
                  >
                    이미지
                  </button>
                  <button
                    type="button"
                    className="text-sm bg-red-50 text-red-500 px-3 py-1 rounded-md"
                    disabled={busyName === category}
                    onClick={() => setDeleteTarget(category)}
                  >
                    삭제
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* 추가·닫기를 모달 맨 아래 한 줄에 나란히 둔다 */}
          <div className="flex gap-2 mt-4">
            <button
              type="submit"
              disabled={isSaving}
              className="px-5 py-2 text-sm bg-gray-900 text-white rounded-2xl disabled:bg-gray-400"
            >
              {isSaving ? "추가 중..." : "추가"}
            </button>
            <button
              type="button"
              onClick={onClose}
              disabled={isSaving}
              className="px-5 py-2 text-sm border border-gray-900 text-gray-900 rounded-2xl hover:bg-gray-900 hover:text-white"
            >
              닫기
            </button>
          </div>
          </form>
        </div>
      </div>

      {deleteTarget && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2>카테고리 삭제</h2>
            <p>'{deleteTarget}' 카테고리를 삭제합니다.</p>
            <p>정말 삭제 하시겠어요?</p>
            <div className="flex gap-2 mt-4">
              <button
                onClick={handleDelete}
                className="px-5 py-2 text-sm bg-red-500 text-white rounded-2xl"
              >
                삭제
              </button>
              <button
                onClick={() => setDeleteTarget(null)}
                className="px-5 py-2 text-sm border border-gray-900 text-gray-900 rounded-2xl hover:bg-gray-900 hover:text-white"
              >
                취소
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CategoryManageModal;
